import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Callout } from '../components/ui/Callout';
import { Icon } from '../components/ui/Icon';

const EmailVerificationSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const { userId, token } = useParams<{ userId: string; token: string }>();
  const { verifyEmail } = useAuth();
  const { translate } = useLanguage();

  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<'success' | 'error' | null>(null);
  const [message, setMessage] = useState('');
  const hasVerifiedRef = useRef(false);

  useEffect(() => {
    const handleVerification = async () => {
      if (hasVerifiedRef.current) return;

      if (!userId || !token) {
        setVerificationStatus('error');
        setMessage('Invalid verification link');
        setIsVerifying(false);
        return;
      }

      hasVerifiedRef.current = true;

      try {
        const response = await verifyEmail(userId, token);
        setVerificationStatus('success');
        setMessage(response.message || 'Email verified successfully!');
      } catch (error) {
        setVerificationStatus('error');
        setMessage('Verification failed. The link may be expired or invalid.');
      } finally {
        setIsVerifying(false);
      }
    };

    handleVerification();
  }, [userId, token, verifyEmail]);

  const handleContinue = useCallback(() => {
    navigate('/login', {
      state: { message: 'Email verified successfully! You can now log in.' }
    });
  }, [navigate]);

  const handleGoHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  if (isVerifying) {
    return (
      <div className="page-tight" style={{ paddingTop: 48, textAlign: 'center' }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 14,
            background: 'var(--bg-tint)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
          }}
        >
          <Icon name="refresh" size={24} />
        </div>
        <h1 style={{ fontSize: 28, letterSpacing: '-0.02em', margin: 0 }}>
          {translate('verifying-email')}
        </h1>
        <p className="muted" style={{ marginTop: 8 }}>
          {translate('please-wait-verification')}
        </p>
      </div>
    );
  }

  const isSuccess = verificationStatus === 'success';

  return (
    <div className="page-tight" style={{ paddingTop: 48, textAlign: 'center' }}>
      {/* Icon badge */}
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 14,
          background: 'var(--bg-tint)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 16,
        }}
      >
        <Icon
          name={isSuccess ? 'checkCircle' : 'alertCircle'}
          size={28}
          color={isSuccess ? 'var(--ok, #059669)' : 'var(--warn, #dc2626)'}
        />
      </div>

      <h1 style={{ fontSize: 28, letterSpacing: '-0.02em', margin: 0 }}>
        {isSuccess
          ? translate('email-verified-success')
          : translate('verification-failed')}
      </h1>

      <p className="muted" style={{ marginTop: 8 }}>
        {isSuccess
          ? translate('email-verification-successful')
          : translate('verification-link-expired')}
      </p>

      <div style={{ marginTop: 16 }}>
        <Callout tone={isSuccess ? 'ok' : 'warn'}>
          {message}
        </Callout>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 24 }}>
        {isSuccess ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleGoHome}
          >
            {translate('go-to-homepage')}
            <Icon name="arrowRight" size={15} />
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleContinue}
            >
              {translate('continue-to-login')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/verify-email')}
            >
              {translate('request-new-link')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationSuccessPage;
